/** @jsx jsx */
import React, {
	type ComponentType,
	cloneElement,
	type ReactNode,
	type ReactElement,
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	useState,
	useMemo,
	useCallback,
} from 'react';
import { styled, jsx } from '@compiled/react';
import isBoolean from 'lodash/isBoolean';
import { Inline, Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { css, cssMap, cx } from '@atlaskit/css';
import {
	getIsSoftwareThemingEnabled,
	getIsSoftwareThemingM2Enabled,
} from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { columnHeaderHeight, layout } from '../../../constants/styles/index.tsx';
import { ColumnTheme, type ColumnId } from '../../../types.tsx';
import { columnThemes } from '../../column/index.tsx';

type Props = {
	isEditingTitle?: boolean;
	isDragging?: boolean;
	isDraggable?: boolean;
	isSpinnerVisible?: boolean;
	isMenuEnabled?: boolean;
	isMenuShown?: boolean | null;
	id: ColumnId;
	title: ReactNode;
	titleHeadingText?: string;
	appearance?: ColumnTheme;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	menu?: ReactElement<ComponentProps<ComponentType<any>>> | null;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	OverlayContent?: ComponentType<any> | undefined;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onClick?: () => any;
	isMatrixLayout?: boolean;
};

const ColumnHeader = ({
	id,
	menu,
	title,
	onClick,
	isDragging,
	appearance,
	isDraggable,
	isMenuShown,
	isMenuEnabled,
	OverlayContent,
	isEditingTitle,
	titleHeadingText,
	isSpinnerVisible,
	isMatrixLayout,
}: Props) => {
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const [isContainerFocused, setIsContainerFocused] = useState<boolean>(false);

	const hasTemporaryId = useMemo(() => Number(id) <= 0, [id]);

	const backgroundColor = useMemo(
		() => getBackgroundColor(appearance ?? ColumnTheme.Default),
		[appearance],
	);

	const isMenuTriggerShown = useMemo(
		() => (isBoolean(isMenuShown) ? isMenuShown : isContainerFocused || isMenuOpen),
		[isMenuShown, isContainerFocused, isMenuOpen],
	);

	const canShowMenu = useMemo(
		() => isMenuEnabled && !hasTemporaryId && !isEditingTitle && !isDragging,
		[isMenuEnabled, isEditingTitle, isDragging, hasTemporaryId],
	);

	const dynamicBackgroundColorStyleOverride = css({
		// As we can't access this div element from our component scope, adding this to avoid button background when focusing on container
		// @ts-expect-error Property Type '{ backgroundColor: string; }' is not assignable to type 'undefined'.ts(2322)
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
			backgroundColor,
		},
	});

	const onMenuOpenChange = useCallback((attrs: { isOpen: boolean }) => {
		setIsMenuOpen(attrs.isOpen);
	}, []);

	const renderMenu = useCallback(
		() =>
			menu ? (
				<div
					css={[
						headerStyleMap.menuWrapperBase,
						canShowMenu && headerStyleMap.menuWrapperVisibility,
						isMenuTriggerShown && headerStyleMap.menuWrapperOpacity,
						// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
						dynamicBackgroundColorStyleOverride,
					]}
					data-testid="platform-board-kit.common.ui.column-header.header.meatball"
				>
					{cloneElement(menu, {
						title: titleHeadingText,
						onOpenChange: onMenuOpenChange,
					})}
				</div>
			) : null,
		[
			canShowMenu,
			dynamicBackgroundColorStyleOverride,
			isMenuTriggerShown,
			menu,
			onMenuOpenChange,
			titleHeadingText,
		],
	);

	const onContainerFocus = useCallback(() => {
		if (canShowMenu) {
			setIsContainerFocused(true);
		}
	}, [canShowMenu]);

	const onContainerBlur = () => {
		setIsContainerFocused(false);
	};

	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box
			onClick={onClick}
			testId="platform-board-kit.common.ui.column-header.header.column-header-container"
			onFocus={onContainerFocus}
			onBlur={onContainerBlur}
			onMouseEnter={onContainerFocus}
			onMouseLeave={onContainerBlur}
			xcss={cx(
				headerStyleMap.columnHeaderContainerBase,
				isDraggable && headerStyleMap.cursorGrab,
				// eslint-disable-next-line no-nested-ternary
				isMatrixLayout
					? getIsSoftwareThemingM2Enabled()
						? headerStyleMap.roundedBox
						: headerStyleMap.roundedBoxOld
					: headerStyleMap.roundedTopBorder,
			)}
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{ backgroundColor }}
		>
			{OverlayContent && <OverlayContent />}
			{isVisualRefreshEnabled() ? (
				<Inline spread="space-between" alignBlock="center" xcss={[ColumnHeaderStyles]}>
					<div css={[headerStyleMap.titleWrapper]}>{title}</div>
					{/* eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable */}
					<Box
						xcss={[!isVisualRefreshEnabled() && menuWrapperStyles]}
						// Prevent menu click to bubble up and fire header analytic event.
						onClick={(event: React.MouseEvent) => event.stopPropagation()}
					>
						{renderMenu()}
						{isSpinnerVisible && (
							<SpinnerContainer>
								<Spinner size="small" />
							</SpinnerContainer>
						)}
					</Box>
				</Inline>
			) : (
				<>
					<TitleWrapperOld>{title}</TitleWrapperOld>
					<ColumnHeaderActionsOld
						// Prevent menu click to bubble up and fire header analytic event.
						onClick={(event: React.MouseEvent) => event.stopPropagation()}
					>
						{renderMenu()}
						{isSpinnerVisible && (
							<SpinnerContainer>
								<Spinner size="small" />
							</SpinnerContainer>
						)}
					</ColumnHeaderActionsOld>
				</>
			)}
		</Box>
	);
};
ColumnHeader.defaultProps = {
	isDragging: false,
	isDraggable: false,
	isSpinnerVisible: false,
	isMenuEnabled: true,
	isEditingTitle: false,
};

export default ColumnHeader;

const getBackgroundColor = (appearance: ColumnTheme): string => {
	return getIsSoftwareThemingEnabled()
		? columnThemes[appearance].backgroundNew
		: columnThemes[appearance].background;
};

const SpinnerContainer = ({ children }: { children: ReactNode }) => (
	<Box xcss={refactoredSpinnerStyle}>{children}</Box>
);

const headerStyleMap = cssMap({
	columnHeaderContainerBase: {
		userSelect: 'none',
		width: '100%',
		maxWidth: '100%',
		boxSizing: 'border-box',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		height: `${columnHeaderHeight}px`,
		position: 'relative',
		transition: 'background-color 0.2s ease',
	},
	cursorGrab: {
		cursor: 'grab',
	},
	menuWrapperBase: {
		visibility: 'hidden',
		opacity: 0,
		width: '0px',
	},
	menuWrapperVisibility: {
		visibility: 'visible',
	},
	menuWrapperOpacity: {
		opacity: 1,
		width: 'auto',
		paddingRight: token('space.050'),
	},
	titleWrapper: {
		flex: '1 1 auto',
		minWidth: `${0}px`,
		'&:focus': {
			boxShadow: token('elevation.shadow.overflow'),
			outline: 'none',
		},
	},
	roundedBox: {
		borderRadius: '6px',
	},
	roundedBoxOld: {
		borderRadius: '6px',
		marginBottom: token('space.100'),
	},
	roundedTopBorder: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		borderTopLeftRadius: `${layout.cardListRadius}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		borderTopRightRadius: `${layout.cardListRadius}px`,
	},
});

const refactoredSpinnerStyle = xcss({ paddingRight: 'space.100' });

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColumnHeaderActionsOld = styled.div({
	display: 'flex',
	position: 'absolute',
	right: token('space.100'),
	top: token('space.300'),
	transform: 'translateY(-50%)',
});

const ColumnHeaderStyles = xcss({
	height: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleWrapperOld = styled.div({
	display: 'flex',
	flexFlow: 'column nowrap',
	justifyContent: 'center',
	alignItems: 'stretch',
	flex: '1 1 auto',
	alignSelf: 'auto',
	maxWidth: '100%',
	'&:focus': {
		boxShadow: `inset 0 0 0 2px ${token('color.border.focused')}`,
		outline: 'none',
	},
	paddingTop: token('space.200'),
	paddingRight: 0,
	paddingBottom: token('space.200'),
	paddingLeft: 0,
	/* ie11 below */
	width: '100%',
	height: `calc(100% - ${token('space.400')})`,
});

const menuWrapperStyles = xcss({
	flexShrink: 0,
	width: token('space.400'),
});
